// ============================================================================
// UIContext — Phase 2.0.6 (+ Phase 3.3 pathname-first init)
// ============================================================================
// Thin Provider for global UI state shared across features.
//
//   view              : string — current top-level view (home, broilerHome, etc.)
//                       Initial value reads URL pathname (Phase 3) — the legacy
//                       hash-bookmark branch is defensive backup for the case
//                       where the Phase 3.3 shim somehow didn't run.
//   pendingEdit       : { id, viewName } | null — pops open an edit modal after nav
//   showMenu          : boolean — mobile side-menu open
// ============================================================================
import {createContext, useCallback, useContext, useState} from 'react';
import type {ReactNode, SetStateAction} from 'react';
import {PATH_TO_VIEW} from '../lib/routes.js';

// Defensive, pre-router fallback only. runtimeInit normally rewrites these
// legacy fragments before React mounts, but freezing every accepted spelling
// protects a fail-soft boot where the History API is unavailable.
export const UI_CONTEXT_HASH_FALLBACK = Object.freeze({
  '#webforms': 'webformhub',
  '#/webforms': 'webformhub',
  '#addfeed': 'addfeed',
  '#/addfeed': 'addfeed',
  '#weighins': 'weighins',
  '#/weighins': 'weighins',
  '#pigdailys': 'webform',
  '#/pigdailys': 'webform',
} as const);

// Annotated `: string` deliberately. Every literal below is already a string,
// but `PATH_TO_VIEW` is built with `Object.fromEntries(...)` over a mapped
// `string[][]`, which resolves to the loose `fromEntries` overload — so the
// pathname lookup arrives untyped. Declaring the return here is the boundary
// where a view name is stated to be a string, instead of that looseness
// spreading into `view` and from there into all 36 consumers of this context.
function initialView(): string {
  if (typeof window === 'undefined') return 'home';
  // Primary: pathname. Phase 3.3 shim runs before this useState initializer,
  // so any legacy hash bookmarks have already been rewritten to clean paths.
  const pathView = PATH_TO_VIEW[window.location.pathname];
  if (pathView) return pathView;
  // Defensive backup: hash (should never fire after 3.3 in normal flow,
  // but if the shim threw for some reason we still honor the bookmark).
  const h = window.location.hash;
  const hashView = UI_CONTEXT_HASH_FALLBACK[h as keyof typeof UI_CONTEXT_HASH_FALLBACK];
  if (hashView) return hashView;
  return 'home';
}

// Value type INFERRED from the builder below. `view` and `showMenu` need no
// annotation at all. `pendingEdit` is documented as `{id, viewName}`, but this
// module only ever stores and hands back the object — it is written by the
// timeline views and read by the record pages — so `unknown` records what this
// module actually knows rather than pinning a shape it never inspects.
function useUIValue() {
  // `viewRequest` counts explicit requests for the view that is ALREADY
  // current. A plain useState setter drops those, but they are real: on the
  // Not Found page the URL no longer matches `view`, so choosing that same
  // destination in the Header must still move the URL (App acts on the count
  // only while its Not Found page is showing). A different view behaves
  // exactly as before.
  const [nav, setNav] = useState(() => ({view: initialView(), viewRequest: 0}));
  const setView = useCallback((next: SetStateAction<string>) => {
    setNav((prev) => {
      const value = typeof next === 'function' ? next(prev.view) : next;
      return value === prev.view
        ? {view: prev.view, viewRequest: prev.viewRequest + 1}
        : {view: value, viewRequest: prev.viewRequest};
    });
  }, []);
  const {view, viewRequest} = nav;
  const [pendingEdit, setPendingEdit] = useState<unknown>(null);
  const [showMenu, setShowMenu] = useState(false);

  return {
    view,
    viewRequest,
    setView,
    pendingEdit,
    setPendingEdit,
    showMenu,
    setShowMenu,
  };
}

type UIContextValue = ReturnType<typeof useUIValue>;

const UIContext = createContext<UIContextValue | null>(null);

export function UIProvider({children}: {children?: ReactNode}) {
  const value = useUIValue();
  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
}

// Asserts the provider, and that assertion is what gives every consumer a
// non-null type. Not a new failure mode: the hook used to return `null` outside
// its provider and every consumer destructures the result immediately, so the
// app ALREADY crashed at that exact line with an anonymous
// `TypeError: Cannot destructure property … of null`. Same failure, same point,
// named cause.
export function useUI(): UIContextValue {
  const value = useContext(UIContext);
  if (!value) throw new Error('useUI must be used within UIProvider');
  return value;
}
